import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Environment, MMobileService } from '@core/services/common';
import { ContextService } from '@core/services/core/context.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { Address, Province } from '@models/address';
import { Category, CollaboratorsFilter, Discount } from '@models/collaborators';
import { TeCuidamosOperation } from '@models/operation-register';
import { TcCard } from '@models/raffle';
import { Service } from '@models/service';
import {
  Clover,
  CloverMovement,
  CloverPolicy,
  Rating,
  TeCuidamos,
  TeCuidamosRegister,
  TeCuidamosUser,
  TeCuidamosUserData,
  TeCuidamosUserType
} from '@models/user';
import { formatDocumentNumber, safeTrim } from '@utils/utils';
import * as moment from 'moment';
import { BehaviorSubject, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { BaseService } from './base.service';
import { NavigationHelper } from './navigation-helper.service';
import { OperationRegisterService } from './operation-register.service';
import { SecureStoreService } from './storage/secure.service';

@Injectable({
  providedIn: 'root'
})
export class TecuidamosService {
  protected readonly DOCUMENTUM_IMGS_PARTIAL_URL_INT_WIP =
    'http://webservices.mapfre.com/mmobile-int/services/public/mets/{APP_VERSION}/documentumImageWIP?imgPath={IMG_PATH}';
  protected readonly DOCUMENTUM_IMGS_PARTIAL_URL_INT =
    'http://webservices.mapfre.com/mmobile-int/services/public/mets/{APP_VERSION}/documentumImage?imgPath={IMG_PATH}';
  protected readonly DOCUMENTUM_IMGS_PARTIAL_URL_PRE_WIP =
    'http://webservices.mapfre.com/mmobile-pre/services/public/mets/{APP_VERSION}/documentumImageWIP?imgPath={IMG_PATH}';
  protected readonly DOCUMENTUM_IMGS_PARTIAL_URL_PRE =
    'http://webservices.mapfre.com/mmobile-pre/services/public/mets/{APP_VERSION}/documentumImage?imgPath={IMG_PATH}';
  protected readonly DOCUMENTUM_IMGS_PARTIAL_URL_PRO_WIP =
    'http://pewip.pro.mapfre.net/tecuidamos';
  protected readonly DOCUMENTUM_IMGS_PARTIAL_URL_PRO =
    'https://www.mapfretecuidamos.com/tecuidamos';
  protected readonly DOCUMENTUM_PDFS_PARTIAL_URL_PRE_WIP =
    'http://webservices.mapfre.com/mmobile-pre/services/public/mets/{APP_VERSION}/documentumPdfWIP?pdfPath={PDF_PATH}';
  protected readonly DOCUMENTUM_PDFS_PARTIAL_URL_PRE =
    'http://webservices.mapfre.com/mmobile-pre/services/public/mets/{APP_VERSION}/documentumPdf?pdfPath={PDF_PATH}';
  protected readonly DOCUMENTUM_PDFS_PARTIAL_URL_PRO_WIP =
    'http://pewip.pro.mapfre.net/tecuidamos';
  protected readonly DOCUMENTUM_PDFS_PARTIAL_URL_PRO =
    'https://www.mapfretecuidamos.com/tecuidamos';

  state: BehaviorSubject<TeCuidamosUserData> = new BehaviorSubject({} as any);

  constructor(
    public ctx: ContextService,
    private http: HttpClient,
    private mmobile: MMobileService,
    private secure: SecureStoreService,
    private ui: UiHelperService,
    private base: BaseService,
    private or: OperationRegisterService,
    private env: Environment,
    private nav: NavigationHelper,
    private iab: InAppBrowser,
    private mmobileHelper: MMobileHelperService
  ) {
    this.init();
  }

  async init() {
    await this.env.ready();
    const stored =
      (await this.secure.get('TECUIDAMOS__STATE').catch(() => {})) || {};
    this.state.next({ ...this.getState(), ...(stored as TeCuidamosUserData) });
  }

  clear() {
    this.state.next({} as TeCuidamosUserData);
  }
  getState$() {
    return this.state.asObservable();
  }
  getState() {
    return this.state.getValue();
  }
  private setState(state: TeCuidamosUserData) {
    const newState = { ...this.getState(), ...state };
    this.state.next(newState);
    this.secure.save('TECUIDAMOS__STATE', newState);
  }

  loginTeCuidamos() {
    return this.getTeCuidamosUserData().pipe(
      switchMap(userData => of(this.setState(userData))),
      catchError(() => of(null))
    );
  }

  isAllowed(memberState: TeCuidamosUserType) {
    const allowed = [TeCuidamosUserType.LOGIN, TeCuidamosUserType.SIGNUP, TeCuidamosUserType.ERROR];
    return allowed.includes(memberState);
  }

  navToTeCuidamos(direction: 'forward' | 'root', to?: TeCuidamosUserType, type?: string, urlParams?: { [param: string]: string }) {
    const navTo = (url: string) => {
      direction === 'forward' ? this.nav.toNew(url) : this.nav.newRoot(url);
    };
    switch (to) {
      case TeCuidamosUserType.LOGIN:
        if (this.ctx.isApp) {
          navTo('/tecuidamos/tc-home');
        } else {
          this.navigateToTeCuidamos(type, urlParams);
        }
        break;
      case TeCuidamosUserType.SIGNUP:
        if (this.ctx.isApp) {
          navTo('/tecuidamos/tc-register-presentation');
        } else {
          this.navigateToTeCuidamos(type, urlParams);
        }
        break;
      case TeCuidamosUserType.CANT:
      case TeCuidamosUserType.ERROR:
        navTo('/tecuidamos/tc-cant-register');
        break;
      default: break;
    }
  }

  private getTeCuidamosUserData() {
    return this.getUserData().pipe(
      switchMap(userData =>
        userData.memberState === TeCuidamosUserType.LOGIN
          ? forkJoin([
              of(userData),
              this.getRating(),
              this.getClovers(),
              this.downloadCardImages()
            ])
          : forkJoin([of(userData), this.getRating(), of({}), of({})])
      ),
      map(
        ([userData, rating, clover, tcCard]) =>
          ({
            ...userData,
            ...clover,
            ...rating,
            ...tcCard
          } as TeCuidamosUserData)
      )
    );
  }

  private getUserData() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients`;
    const params = this.or.calculateTeCuidamosParams();
    return this.http
      .get<TeCuidamos>(url, {
        ...this.base.getPrivateOIMHeaders(),
        params
      })
      .pipe(
        catchError(() =>
          of({ memberState: TeCuidamosUserType.ERROR } as TeCuidamos)
        )
      );
  }

  updateTecuidamosClientData(userEmail: string, userPhone: string) {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/personalData`;
    const body = {
      email: userEmail,
      cellphoneNumber: userPhone
    };
    return this.http.post(url, body, this.base.getPrivateOIMHeaders());
  }

  getRoadTypes() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/codedValues`;
    return this.http.get<any>(url, this.base.getPrivateOIMHeaders());
  }

  getAciCodeToTcRoadTypeTranslation(aciCode: string) {
    switch (aciCode) {
      case 'AR':
        return 'ARR';
      case 'CLL':
        return 'CL';
      case 'CJ':
        return 'CJN';
      case 'CH':
        return 'CHA';
      case 'RTD':
        return 'ROT';
      default:
        return null;
    }
  }

  updateUser() {
    return this.getTeCuidamosUserData().subscribe(
      (clientsData: TeCuidamosUserData) => {
        this.setState(clientsData);
      },
      catchError(err => {
        return throwError(err);
      })
    );
  }
  addClient(tcData: TeCuidamosRegister, headerParams?: any) {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients`;
    const body: any = {
      documentId: formatDocumentNumber(tcData.documentNumber),
      name: safeTrim(tcData.name),
      surname1: safeTrim(tcData.surname1),
      birthDate: tcData.date ? moment(tcData.date).format('DD/MM/YYYY') : '',
      gender: safeTrim(tcData.sex),
      roadType: tcData.streetType,
      roadName: safeTrim(tcData.street),
      roadNumber: tcData.number,
      postalCode: tcData.postalCode,
      municipality: safeTrim(tcData.location),
      email: safeTrim(tcData.email),
      cellphoneNumber: safeTrim(tcData.mobile)
    };
    if (tcData.surname2) {
      body.surname2 = safeTrim(tcData.surname2);
    }
    if (tcData.status) {
      body.maritalStatus = safeTrim(tcData.status);
    }
    if (tcData.additionalAddressInfo) {
      body.additionalAddressInfo = safeTrim(tcData.additionalAddressInfo);
    }
    if (tcData.phone) {
      body.phoneNumber = safeTrim(tcData.phone);
    }
    const headers = this.base.getPrivateOIMHeaders();
    return this.http
      .post<TeCuidamosUserData>(url, body, { ...headers, params: headerParams })
      .pipe(
        tap((clientsData: TeCuidamosUserData) => this.setState(clientsData))
      );
  }

  composeAddressAdditionalInfo(address: Address) {
    let additionalInfo = '';
    if (!address) {
      return additionalInfo;
    }
    additionalInfo += address.portal ? ` ${address.portal}` : '';
    additionalInfo += address.stair ? ` ${address.stair}` : '';
    additionalInfo += address.floor ? ` ${address.floor}` : '';
    additionalInfo += address.door ? ` ${address.door}` : '';
    additionalInfo += address.additionalInfo
      ? ` ${address.additionalInfo}`
      : '';
    return additionalInfo;
  }

  getProvincesList() {
    const url = 'assets/constants/provincesList.json';
    return this.http.get<Province[]>(url);
  }

  private getRating() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/customers/rating`;
    return this.http
      .get<Rating>(url, this.base.getPrivateOIMHeaders())
      .pipe(catchError(() => of({} as Rating)));
  }

  getCollaboratorTypes(isNewEndpointEnable: boolean) {
    if (!isNewEndpointEnable) {
      return of([
        {
          id: '1',
          name: 'Gasolineras',
          enable: true
        },
        {
          id: '2',
          name: 'Talleres distinguidos',
          enable: true
        },
        {
          id: '3',
          name: 'Resto',
          enable: true
        }
      ] as Category[]);
    } else {
      const url = `${this.base.getPublicServicesLocator()}/servicesLocator/collaborators/categories`;
      const headers = this.base.getPrivateOIMHeaders();
      return this.http
        .get<Category[]>(url, headers)
        .pipe(
          map((collaboratorList: Category[]) =>
            this.mapFilterTypes(collaboratorList)
          )
        );
    }
  }

  getDiscountTypes(isNewEndpointEnable: boolean) {
    if (!isNewEndpointEnable) {
      return of([
        {
          id: '1',
          name: 'Tarjeta de crédito teCuidamos',
          enable: true
        },
        {
          id: '2',
          name: 'Carnet de socio teCuidamos',
          enable: true
        },
        {
          id: '4',
          name: 'NIF',
          enable: true
        }
      ] as Category[]);
    } else {
      const url = `${this.base.getPublicServicesLocator()}/servicesLocator/collaborators/discounts`;
      const headers = this.base.getPrivateOIMHeaders();
      return this.http
        .get<Category[]>(url, headers)
        .pipe(
          map((discountList: Category[]) => this.mapFilterTypes(discountList))
        );
    }
  }

  private mapFilterTypes(categoryList: Category[]) {
    return categoryList.map((category: Category) => ({
      ...category,
      enable: true
    }));
  }

  async getDiscountCenters(
    radius: number,
    pageSize: number,
    pageNum: number,
    filter: CollaboratorsFilter,
    isNewEndpointEnable: boolean,
    lat?: number,
    lng?: number
  ) {
    const url = isNewEndpointEnable
      ? `${this.base.getPublicServicesLocator()}/servicesLocator/collaborators/centers`
      : `${this.base.getPublicBaseUrl()}/servicesLocator/entitiesNewCo`;
    const enableGarages = filter.collaboratorTypes.find(
      collaboratorType =>
        collaboratorType &&
        (collaboratorType.id === 'T' || collaboratorType.id === '2')
    ).enable;
    let httpParams = new HttpParams()
      .set('useNew', 'false')
      .set('lng', `${lng}`)
      .set('lat', `${lat}`)
      .set('radius', `${radius}`)
      .set('page', `${pageNum}`)
      .set('size', `${pageSize}`)
      .set('collaborators', 'true')
      .set('lossAdjCenters', 'false')
      .set('medTeams', 'false')
      .set('offices', 'false')
      .set('policlinics', 'false')
      .set('medTeamProduct', '')
      .set('medTeamSpecialty', '0')
      .set('garEspeci', '')
      .set('garMarca', '')
      .set('garVentaja', '')
      .set('garages', enableGarages.toString());

    httpParams = this.fillWithFilters(httpParams, filter);

    const options = this.base.getPublicHeaders();
    options.params = httpParams;
    return this.http.get<Discount[]>(url, options).toPromise();
  }

  async getDiscount(key: string, type: string, isNewEndpointEnable: boolean) {
    const url = isNewEndpointEnable
      ? `${this.base.getPublicServicesLocator()}/servicesLocator/collaborators/centers/${key}`
      : `${this.base.getPublicBaseUrl()}/servicesLocator/entitiesNewCo/${key}`;
    const httpParams = new HttpParams().set('type', `${type}`);
    const options = this.base.getPublicHeaders({ cacheResponse: true });
    options.params = httpParams;
    return this.http.get<Discount>(url, options).toPromise();
  }

  fillWithFilters(httpParams: HttpParams, filter: CollaboratorsFilter) {
    if (!filter) return httpParams;

    httpParams = httpParams.set(
      'collaboratorCategory',
      this.getEnabledCategoriesIdsToString(filter.collaboratorTypes)
    );
    httpParams = httpParams.set(
      'collaboratorDiscount',
      this.getEnabledCategoriesIdsToString(filter.discountTypes)
    );

    return httpParams;
  }

  private getEnabledCategoriesIdsToString(filtertypes: Category[]) {
    return filtertypes
      .filter(({ enable }) => enable === true)
      .map(({ id }) => id)
      .join();
  }

  private getClovers() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/clovers`;
    return this.http.get<Clover>(url, this.base.getPrivateOIMHeaders()).pipe(
      catchError(err => {
        const structure =
          err && err.requestId ? `requestId: ${err.requestId}` : '';
        this.or.registerTeCuidamosOperation(
          TeCuidamosOperation.ERROR_SAVING,
          structure
        );
        return of({} as Clover);
      })
    );
  }

  getCloversMovements(initDate: Date, finishDate: Date, page?: number) {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/clovers/transactions?dateSince=${this.formatDate(
      initDate
    )}&dateTo=${this.formatDate(finishDate)}&page=${page || 1}`;

    return this.http
      .get<CloverMovement>(url, this.base.getPrivateOIMHeaders())
      .pipe(map(res => this.sliceDates(res)));
  }

  formatDate(date: Date) {
    return `${date.getFullYear()}${('0' + (date.getMonth() + 1)).slice(-2)}${(
      '0' + date.getDate()
    ).slice(-2)}`;
  }

  sliceDates(list: CloverMovement) {
    for (const t of list.transactionList) {
      let aux = '';
      t.date = t.date.toString();
      aux = `${t.date.substr(0, 4)}/${t.date.substr(4, 2)}/${t.date.substr(
        6,
        2
      )}`;
      t.date = aux;
    }
    return list;
  }

  getCloversPolicies() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/clovers/policies`;
    return this.http.get<CloverPolicy[]>(url, this.base.getPrivateOIMHeaders());
  }

  toggleCloverPolicy(policyNumber: string, toggle: boolean) {
    return this._toggleCloverPolicy(policyNumber, toggle).pipe(
      tap(() =>
        this.getClovers().subscribe((clovers: TeCuidamosUserData) =>
          this.setState(clovers)
        )
      )
    );
  }

  private _toggleCloverPolicy(policyNumber: string, toggle: boolean) {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/clovers/policies`;
    return this.http.post(
      url,
      [{ policyNumber, selected: toggle }],
      this.base.getPrivateOIMHeaders()
    );
  }

  getServices() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/services`;
    return this.http.get<Service[]>(url, this.base.getPrivateOIMHeaders());
  }

  getRaffles() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/raffles`;
    return this.http.get(url, this.base.getPrivateOIMHeaders());
  }

  private downloadCardImages() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/cardImages`;
    return this.http.get<TcCard>(url, this.base.getPrivateOIMHeaders()).pipe(
      tap(() =>
        this.or.registerTeCuidamosOperation(TeCuidamosOperation.CARD, '')
      ),
      catchError(() => {
        this.or.registerTeCuidamosOperation(TeCuidamosOperation.ERROR_CARD, '');
        return of({} as TcCard);
      })
    );
  }


  subscribeToRaffles(raffles: any[], email: string, cellphoneNumber: string) {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/clients/raffles`;
    return this.http.post(
      url,
      { email, cellphoneNumber, raffles },
      this.base.getPrivateOIMHeaders()
    );
  }

  getImageUrl(imagePath: string) {
    const baseUrl = this.mmobile.getServiceUrl('documentumImage');
    return `${baseUrl}?imgPath=${imagePath}`;
  }

  getPdfUrl(pdfPath: string) {
    const baseUrl = this.mmobile.getServiceUrl('documentumPdf');
    return `${baseUrl}?pdfPath=${pdfPath.substr(pdfPath.lastIndexOf('/') + 1)}`;
  }

  async navigateToTeCuidamos(product: string, urlParams?: { [param: string]: string }) {
    await this.ui.presentLoading('teCuidamos-sso');
    const url = this.mmobileHelper.getCustomConfigData('TE_CUIDAMOS', 'subscribeTeCuidamos');
    const link = await this.getClubMapfreUrlWithParams(url, product, urlParams).toPromise().catch(() => null as string);
    this.ui.dismissLoading('teCuidamos-sso');
    if (link) {
      this.iab.create(link, '_self');
      this.nav.goHomePage();
    } else {
      this.nav.to('/tecuidamos/tc-cant-register');
    }
  }

  getClientInformation(hashTC: string){
    const url = `${this.base.getPublicBaseUrl()}/teCuidamos/clients/clientInformation?hash=${hashTC}`;
    return this.http.get<TeCuidamosUser>(url, this.base.getPublicHeaders());
  }


  getClubMapfreUrlWithParams(link: string, product: string, urlParams?: { [param: string]: string }) {
    return this.getSaviaToken().pipe(
      switchMap(data => {
        const url = `${this.base.getTeCuidamosSaviaBaseUrl()}/aci/sso/login`;
        const { validationToken, ...body } = data;
        const headers = new HttpHeaders()
          .append('Content-Type', 'application/json')
          .append('Authorization', `Bearer ${validationToken}`);

        return this.http.post<{token: string}>(url, body, { headers })
          .pipe(
            catchError(err => {
              this.or.registerTeCuidamosSsoOperation(product, TeCuidamosOperation.SSO_KO, err);
              return throwError(err);
            }),
            map(res => {
              this.or.registerTeCuidamosSsoOperation(product, TeCuidamosOperation.SSO_OK);
              const openUrl = new URL(link);
              const origin = this.ctx.isApp ? 'metsapp' : 'metsweb';
              openUrl.searchParams.set('origin', origin);
              openUrl.searchParams.set('token', res.token);
              if (urlParams) {
                Object.entries(urlParams).forEach(([key, value]) => {
                  openUrl.searchParams.set(key, value);
                });
              }
              return openUrl.href;
            })
          );
      })
    );
  }

  getLoyaliaSsoToken(){
    return this.getSaviaToken().pipe(
      switchMap(data => {
        const url = `${this.base.getTeCuidamosSaviaBaseUrl()}/loyalia/authentication`;
        const headers = new HttpHeaders()
          .append('Authorization', `Bearer ${data.validationToken}`);
        return this.http.post<{token: string}>(url, { document: data.document }, { headers });
      })
    );
  }

  private getSaviaToken() {
    const url = `${this.base.getPrivateTeCuidamosBaseUrl()}/sso/multimapTeCuidamos/loginToken`;
    return this.http.post<any>(url, {}, this.base.getPrivateOIMHeaders());
  }

}
